import { image } from "d3";
import _ from "lodash";
import { useEffect, useRef, useState } from "react"
import { Button, Card, Dropdown, DropdownItemProps, Form, FormProps, Header, Image, Input, Modal, Pagination, Segment, SegmentGroup, Table, TextArea } from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { hostUrl, httpRequest, restRequest } from "../http";
import { CommentContainerType } from "../models/comment_model";
import { FileContainerType, PageData } from "../models/common_model";
import { FailReport, ProductConfig } from "../models/fail_report_model";
import { fieldError } from "../utils";
import { Comments } from "./commons/Comments";
import { ImagesInput } from "./commons/ImagesInput";
import { Page } from "./commons/Page";

export function FailReportComponent(props: {}) {
    const currentUser = useCurrentUser();
    const [searchData, setSearchField] = useFormData<any>({});
    const [reports, setSearchData] = useFormConnect<any, PageData<FailReport>>({}, 'fail_reports/search');

    const editModalControl = useModalControl();
    const viewModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);

    const [currentViewId, setCurrentViewId] = useState<number>();
    const [productConfig, setProductConfig] = useState<ProductConfig>();

    useEffect(() => {
        restRequest<ProductConfig>('fail_reports/product_config').then(setProductConfig)
    }, []);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Name" name="name" value={searchData['name'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <Form.Field inline>
                            <Dropdown options={[{ key: 'all', text: 'All', value: '' }].concat(productConfig?.products.map(p => ({ key: p, text: p, value: p })) || [])} placeholder="Product" name="product" value={searchData['product'] || ''} onChange={setSearchField} search selection fluid />
                        </Form.Field>
                        <Form.Field inline>
                            <Input placeholder="Serial" name="serial" value={searchData['serial'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <Form.Field inline>
                            <Dropdown options={[{ key: 'all', text: 'All', value: '' }].concat(productConfig?.test_stations.map(t => ({ key: t, text: t, value: t })) || [])} placeholder="Testing Station" name="testing_station" value={searchData['testing_station'] || ''} onChange={setSearchField} search selection fluid />
                        </Form.Field>
                        <div><Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Name</th>
                            <th>Product</th>
                            <th>Serial</th>
                            <th>Created Time</th>
                            <th>{currentUser.permissions.fail_report.create && <Button color="blue" icon="add" content="fail report" size="mini" onClick={e => {
                                setCurrentEditId(undefined);
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {reports?.items.map(report => {
                            return <tr key={report.id}>
                                <td>{report.name}</td>
                                <td>{report.product}</td>
                                <td>{report.serial}</td>
                                <td>{report.created_at}</td>
                                <td>
                                    {currentUser.permissions.fail_report.view && <Button onClick={e => {
                                        setCurrentViewId(report.id);
                                        viewModalControl.setOpen(true);
                                    }} color="blue" icon="search" size="mini" />}
                                    {currentUser.permissions.fail_report.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(report.name, argee => {
                                            if (argee) {
                                                return restRequest(`fail_reports/${report.id}`, null, 'delete').then(() => {
                                                    setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                })
                                            }
                                            return Promise.resolve()
                                        })
                                    }} color="red" icon="delete" size="mini" />}
                                    {currentUser.permissions.fail_report.update && <Button onClick={e => {
                                        setCurrentEditId(report.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={reports} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor id={currentEditId} modelControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }} productConfig={productConfig}></Editor>

        {currentViewId && <View id={currentViewId} modalControl={viewModalControl} reloadNumber={reloadNumber}></View>}
    </>
}

function Editor(props: { id?: number, modelControl: ModalControlData, onFinish: (data: FailReport) => void, productConfig?: ProductConfig }) {
    const { id, productConfig } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<FailReport>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setData({} as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<FailReport>('fail_reports/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<FailReport>(`fail_reports/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            props.onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }

    let measurementOptions: DropdownItemProps[] = [];
    if (data['product'] && data['testing_station'] && productConfig?.measurements[data['product']] && productConfig?.measurements[data['product']][data['testing_station']]) {
        measurementOptions = productConfig?.measurements[data['product']][data['testing_station']].map(m => ({ key: m, text: m, value: m }))
    }

    return <>
        <Modal {...props.modelControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Fail Report </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    {isCreate && <>
                        <Form.Group widths="equal">
                            <Form.Input label="Name" name="name" value={data['name'] || ''} onChange={setField} required error={fieldError('name', invalidError)}>
                            </Form.Input>
                            <Form.Input label="Product" required error={fieldError('product', invalidError)}>
                                <Dropdown name="product" value={data['product'] || ''} onChange={setField} options={productConfig?.products.map(p => ({ key: p, value: p, text: p })) || []} search selection fluid></Dropdown>
                            </Form.Input>
                            <Form.Input label="Serial" name="serial" value={data['serial'] || ''} onChange={setField} required error={fieldError('serial', invalidError)}>
                            </Form.Input>
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input label="Test Station" required error={fieldError('testing_station', invalidError)}>
                                <Dropdown name="testing_station" value={data['testing_station'] || ''} onChange={setField} options={productConfig?.test_stations.map(s => ({ key: s, text: s, value: s })) || []} search selection fluid></Dropdown>
                            </Form.Input>
                            <Form.Input label="FW Version" required name="fw_version" value={data['fw_version'] || ''} onChange={setField} error={fieldError('fw_version', invalidError)}>
                            </Form.Input>
                            <Form.Input label="HW Version" name="hw_version" value={data['hw_version'] || ''} onChange={setField} required error={fieldError('hw_version', invalidError)}>
                            </Form.Input>
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input label="Fail Measurement" required error={fieldError('fail_measurement', invalidError)}>
                                <Dropdown options={measurementOptions} name="fail_measurement" value={data['fail_measurement'] || ''} onChange={setField} search selection fluid />
                            </Form.Input>
                        </Form.Group>
                    </>}
                    <Form.Group widths="equal">
                        <Form.Input label="Root Cause">
                            <TextArea name="root_cause" value={data.root_cause || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Fix">
                            <TextArea name="fix" value={data['fix'] || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Pictures">
                            <ImagesInput name="images" value={data.images || []} onChange={setField} containerType={FileContainerType.FailReportPhoto} containerId={id || 0}></ImagesInput>
                        </Form.Input>
                    </Form.Group>
                    <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modelControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}


function View(props: { id: number, modalControl: ModalControlData, reloadNumber: number }) {
    const currentUser = useCurrentUser();
    const { id, reloadNumber, modalControl } = props;
    const [data, setData] = useState<FailReport>({} as any);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setData({} as any);
        setLoading(true);
        restRequest<FailReport>('fail_reports/' + id).then(setData).finally(() => setLoading(false))
    }, [id, reloadNumber])

    return <>
        <Modal {...modalControl.props} size="large" closeIcon>
            <Modal.Header>{data.name || ''}</Modal.Header>
            <Modal.Content>
                <Form loading={loading}>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Name</label>
                            <ViewField>{data.name || ''}</ViewField>
                        </Form.Field>
                        <Form.Field>
                            <label>Product</label>
                            <ViewField>{data.product || ''}</ViewField>
                        </Form.Field>
                        <Form.Field>
                            <label>Serial</label>
                            <ViewField>{data.serial || ''}</ViewField>
                        </Form.Field>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Testing Station</label>
                            <ViewField>{data.testing_station || ''}</ViewField>
                        </Form.Field>
                        <Form.Field>
                            <label>FW Version</label>
                            <ViewField>{data.fw_version || ''}</ViewField>
                        </Form.Field>
                        <Form.Field>
                            <label>HW Version</label>
                            <ViewField>{data.hw_version || ''}</ViewField>
                        </Form.Field>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Fail Measurement</label>
                            <ViewField>{data.fail_measurement || ''}</ViewField>
                        </Form.Field>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Root Cause</label>
                            <ViewField>{data.root_cause || ''}</ViewField>
                        </Form.Field>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Fix</label>
                            <ViewField>{data.fix || ''}</ViewField>
                        </Form.Field>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Picture</label>
                            <Image.Group>
                                {(data.images || []).map(image => {
                                    return <img key={image.id} width="100%" src={hostUrl('files/' + image.id)}></img>
                                })}
                            </Image.Group>
                        </Form.Field>
                    </Form.Group>
                </Form>
                {currentUser.permissions.fail_report.comment && <Comments containerType={CommentContainerType.FailReportComment} containerId={id}></Comments>}
            </Modal.Content>
            <Modal.Actions>
                <Button className="cancel" onClick={e => modalControl.setOpen(false)}>Close</Button>
            </Modal.Actions>
        </Modal>
    </>
}


function ViewField(props: { children: any }) {
    return <div className="ui input">{props.children}</div>
}